import { useEffect, useRef, useState } from 'react';
import { ScoreCategory, useScore } from '../../../context/ScoreContext';

function useAnimatedNumber(target: number, duration = 800) {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);
  useEffect(() => {
    const start = prevRef.current;
    const diff = target - start;
    if (diff === 0) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + diff * eased));
      if (p < 1) requestAnimationFrame(animate);
      else prevRef.current = target;
    };
    requestAnimationFrame(animate);
  }, [target, duration]);
  return display;
}

function useFlashKey(value: number) {
  const [key, setKey] = useState(0);
  const prev = useRef(value);
  useEffect(() => {
    if (value > prev.current) setKey(k => k + 1);
    prev.current = value;
  }, [value]);
  return key;
}

const CATEGORIES: { key: ScoreCategory; label: string; icon: string; color: string }[] = [
  { key: 'battle',    label: '戰鬥積分', icon: 'ri-boxing-line',  color: '#e05555' },
  { key: 'chemistry', label: '默契加分', icon: 'ri-heart-2-line', color: '#d4a843' },
];

function PKBar({
  label, icon, color,
  blackVal, whiteVal,
}: {
  label: string; icon: string; color: string;
  blackVal: number; whiteVal: number;
}) {
  const animB = useAnimatedNumber(blackVal);
  const animW = useAnimatedNumber(whiteVal);
  const total = blackVal + whiteVal || 1;
  const bPct = (blackVal / total) * 100;
  const wPct = (whiteVal / total) * 100;
  const bWin = blackVal > whiteVal;
  const wWin = whiteVal > blackVal;
  const bFlashKey = useFlashKey(blackVal);
  const wFlashKey = useFlashKey(whiteVal);

  return (
    <div className="mb-5">
      <div className="flex items-center justify-center gap-2 mb-3">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className={`${icon} text-sm`} style={{ color }} />
        </div>
        <span className="text-[#888] text-xs tracking-widest uppercase">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span
          key={bFlashKey}
          className={`text-lg font-black w-10 text-right tabular-nums transition-colors duration-500 font-orbitron ${bFlashKey > 0 ? 'score-flash' : ''}`}
          style={{ color: bWin ? '#d4a843' : '#555' }}
        >
          {animB}
        </span>
        <div className="flex-1 h-5 bg-[#111] rounded-full overflow-hidden flex relative border border-[#1e1e1e]">
          <div
            className="h-full rounded-l-full transition-all duration-700 relative"
            style={{
              width: `${bPct}%`,
              background: bWin ? 'linear-gradient(90deg, #9a7825, #d4a843)' : 'linear-gradient(90deg, #2a2a2a, #3a3a3a)',
            }}
          >
            {bWin && <div className="absolute right-0 top-0 h-full w-0.5 bg-[#e8c060]" />}
          </div>
          <div
            className="h-full rounded-r-full transition-all duration-700 ml-auto"
            style={{
              width: `${wPct}%`,
              background: wWin ? 'linear-gradient(90deg, #aaa, #e8e8e8)' : 'linear-gradient(90deg, #2a2a2a, #333)',
            }}
          />
          <div className="absolute left-1/2 top-0 h-full w-px bg-[#1a1a1a] -translate-x-1/2 z-10" />
        </div>
        <span
          key={wFlashKey + 1000}
          className={`text-lg font-black w-10 tabular-nums transition-colors duration-500 font-orbitron ${wFlashKey > 0 ? 'score-flash' : ''}`}
          style={{ color: wWin ? '#e0e0e0' : '#555' }}
        >
          {animW}
        </span>
      </div>
      <div className="flex justify-between px-14 mt-1">
        {bWin && <span className="text-[10px] text-[#d4a843]/60 tracking-widest">領先</span>}
        {wWin && <span className="text-[10px] text-white/40 tracking-widest ml-auto">領先</span>}
      </div>
    </div>
  );
}

export default function PKBattleSection() {
  const { scores, totalBlack, totalWhite } = useScore();

  const animB = useAnimatedNumber(totalBlack);
  const animW = useAnimatedNumber(totalWhite);
  const bTotalFlashKey = useFlashKey(totalBlack);
  const wTotalFlashKey = useFlashKey(totalWhite);

  const isBlackWin = totalBlack > totalWhite;
  const isWhiteWin = totalWhite > totalBlack;
  const isTied = totalBlack === totalWhite;
  const gap = Math.abs(totalBlack - totalWhite);
  const total = totalBlack + totalWhite || 1;

  const gapIntensity = isTied ? 0 : Math.min(gap / 30, 1);
  const leaderRgb  = isBlackWin ? '212,168,67' : '232,232,232';
  const leaderHex  = isBlackWin ? '#d4a843'    : '#e8e8e8';

  const borderAlpha  = (0.25 + gapIntensity * 0.60).toFixed(2);
  const bannerBorder = isTied ? '#2a2a2a' : `rgba(${leaderRgb},${borderAlpha})`;
  const shadowBlur   = Math.round(gapIntensity * 80);
  const shadowAlpha  = (gapIntensity * 0.30).toFixed(2);
  const bannerShadow = isTied ? 'none' : `0 0 ${shadowBlur}px rgba(${leaderRgb},${shadowAlpha})`;

  const bgStop = isBlackWin
    ? `rgba(${Math.round(26 + gapIntensity * 20)},${Math.round(18 + gapIntensity * 8)},0,1)`
    : `rgba(${Math.round(24 + gapIntensity * 20)},${Math.round(24 + gapIntensity * 12)},${Math.round(24 + gapIntensity * 12)},1)`;
  const bannerBg = isTied ? '#0f0f0f' : `linear-gradient(135deg, ${bgStop} 0%, #0d0d0d 65%)`;

  const radialAlpha  = (gapIntensity * 0.22).toFixed(2);
  const topLineAlpha = (0.35 + gapIntensity * 0.65).toFixed(2);

  const dominanceLabel = isTied  ? null
    : gap <= 4  ? { text: 'SLIGHT LEAD',  color: `rgba(${leaderRgb},0.45)` }
    : gap <= 12 ? { text: 'LEADING',      color: `rgba(${leaderRgb},0.60)` }
    : gap <= 22 ? { text: 'DOMINANT',     color: `rgba(${leaderRgb},0.80)` }
    :             { text: 'OVERWHELMING', color: leaderHex };

  const [flash, setFlash] = useState(false);
  const prevTotal = useRef(totalBlack + totalWhite);
  useEffect(() => {
    const cur = totalBlack + totalWhite;
    if (cur !== prevTotal.current) {
      prevTotal.current = cur;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(t);
    }
  }, [totalBlack, totalWhite]);

  void total;

  return (
    <section id="pk" className="relative overflow-hidden py-14 sm:py-20 px-5 sm:px-6 border-t border-[#16172a]" style={{ background: '#06060f' }}>

      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Dot grid — unified gold */}
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(212,168,67,0.04) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.35), transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.15), transparent)' }} />
        <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#d4a843]/25" />
        <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-[#d4a843]/25" />
        <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-[#d4a843]/15" />
        <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#d4a843]/15" />
        <div className="absolute top-1/2 -left-20 w-48 h-48 -translate-y-1/2 rounded-full opacity-8 blur-3xl" style={{ background: 'rgba(212,168,67,0.4)' }} />
        <div className="absolute top-1/2 -right-20 w-48 h-48 -translate-y-1/2 rounded-full opacity-8 blur-3xl" style={{ background: 'rgba(212,168,67,0.3)' }} />
      </div>

      <div className="max-w-4xl mx-auto">

        {/* Section title — unified pill style */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-16 sm:w-24" style={{ background: 'linear-gradient(90deg, transparent, #d4a84340)' }} />
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full" style={{ background: '#d4a84312', border: '1px solid #d4a84335' }}>
              <i className="ri-sword-2-line text-[#d4a843] text-xs" />
              <span className="text-[#d4a843] text-[10px] tracking-[0.4em] uppercase font-black font-cinzel">Battle Statistics</span>
            </div>
            <div className="h-px w-16 sm:w-24" style={{ background: 'linear-gradient(90deg, #d4a84340, transparent)' }} />
          </div>
          <h2 className="text-3xl font-black text-white mb-4 font-serif">戰況即時統計</h2>
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-8" style={{ background: '#d4a84340' }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#d4a843', boxShadow: '0 0 6px #d4a843' }} />
            <div className="h-px w-8" style={{ background: '#d4a84340' }} />
          </div>
        </div>

        {/* Champion banner */}
        <div
          className={`relative rounded-xl border overflow-hidden mb-5 sm:mb-8 transition-all duration-700 ${flash ? 'ring-1 ring-[#d4a843]/40' : ''}`}
          style={{ borderColor: bannerBorder, background: bannerBg, boxShadow: bannerShadow, transition: 'border-color 0.8s, box-shadow 0.8s, background 0.8s' }}
        >
          {/* Top glow line */}
          <div
            className="absolute top-0 left-0 right-0 h-0.5 transition-all duration-700"
            style={{ background: isTied ? 'transparent' : `linear-gradient(90deg, transparent, rgba(${leaderRgb},${topLineAlpha}), transparent)` }}
          />

          {/* Radial glow */}
          {!isTied && (
            <div
              className="absolute inset-0 pointer-events-none transition-all duration-700"
              style={{ background: `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(${leaderRgb},${radialAlpha}) 0%, transparent 70%)` }}
            />
          )}

          {/* Corner accents */}
          {!isTied && gapIntensity > 0.4 && (
            <>
              <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 transition-all duration-700" style={{ borderColor: `rgba(${leaderRgb},${(gapIntensity * 0.6).toFixed(2)})` }} />
              <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 transition-all duration-700" style={{ borderColor: `rgba(${leaderRgb},${(gapIntensity * 0.6).toFixed(2)})` }} />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 transition-all duration-700" style={{ borderColor: `rgba(${leaderRgb},${(gapIntensity * 0.4).toFixed(2)})` }} />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 transition-all duration-700" style={{ borderColor: `rgba(${leaderRgb},${(gapIntensity * 0.4).toFixed(2)})` }} />
            </>
          )}

          <div className="p-4 sm:p-5 md:p-8">
            {/* ── Mobile layout ── */}
            <div className="flex flex-col gap-4 md:hidden">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {isTied ? (
                    <div className="w-12 h-12 rounded-full border-2 border-[#2a2a2a] flex items-center justify-center">
                      <i className="ri-scales-3-line text-2xl text-[#444]" />
                    </div>
                  ) : (
                    <div
                      className="w-12 h-12 rounded-full border-2 flex items-center justify-center relative transition-all duration-700"
                      style={{ borderColor: leaderHex, background: `rgba(${leaderRgb},${(0.08 + gapIntensity * 0.12).toFixed(2)})`, boxShadow: `0 0 ${Math.round(gapIntensity * 20)}px rgba(${leaderRgb},${(gapIntensity * 0.4).toFixed(2)})` }}
                    >
                      <i className="ri-crown-fill text-2xl" style={{ color: leaderHex }} />
                      <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: leaderHex }}>
                        <span className="text-[#0d0d0d] text-[8px] font-black">#1</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {isTied ? (
                    <>
                      <div className="text-[#555] text-[10px] tracking-[0.3em] uppercase mb-0.5">Current Standing</div>
                      <div className="text-white text-lg font-black leading-tight font-serif">勢均力敵</div>
                      <div className="text-[#555] text-xs mt-0.5">兩隊積分持平</div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: leaderHex }}>
                          {isBlackWin ? 'Black' : 'White'} Team · 領先
                        </div>
                        {dominanceLabel && (
                          <span className="text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded-full border font-cinzel" style={{ color: dominanceLabel.color, borderColor: dominanceLabel.color, background: `rgba(${leaderRgb},0.08)` }}>
                            {dominanceLabel.text}
                          </span>
                        )}
                      </div>
                      <div className="text-white text-lg font-black leading-tight font-serif">
                        {isBlackWin ? '黑隊' : '白隊'}
                        <span className="text-xs font-normal text-[#666] ml-1.5">目前領先</span>
                      </div>
                      <div className="text-[#666] text-xs mt-0.5 flex items-center gap-1.5">
                        領先{' '}
                        <span
                          className="font-black tabular-nums transition-all duration-500 font-orbitron"
                          style={{ color: leaderHex, fontSize: `${0.75 + gapIntensity * 0.35}rem` }}
                        >
                          {gap}
                        </span>
                        {' '}分
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Score chips — mobile */}
              <div className="flex gap-2">
                <div
                  className="flex-1 rounded-lg px-3 py-2.5 text-center border transition-all duration-500"
                  style={{ borderColor: isBlackWin ? `rgba(212,168,67,${borderAlpha})` : '#d4a84330', background: isBlackWin ? `rgba(212,168,67,${(0.06 + gapIntensity * 0.12).toFixed(2)})` : '#0f0f0f' }}
                >
                  <div
                    key={bTotalFlashKey}
                    className={`text-2xl font-black tabular-nums font-orbitron ${bTotalFlashKey > 0 ? 'score-flash' : ''}`}
                    style={{ color: isBlackWin ? '#d4a843' : '#a07830', opacity: isBlackWin ? 1 : 0.55 }}
                  >
                    {animB}
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#d4a843]" style={{ opacity: isBlackWin ? 1 : 0.4 }} />
                    <span className="text-[#555] text-[9px] tracking-widest">黑隊</span>
                  </div>
                </div>

                <div
                  className="flex-1 rounded-lg px-3 py-2.5 text-center border transition-all duration-500"
                  style={{ borderColor: isWhiteWin ? `rgba(232,232,232,${borderAlpha})` : '#e0e0e025', background: isWhiteWin ? `rgba(255,255,255,${(0.04 + gapIntensity * 0.10).toFixed(2)})` : '#0f0f0f' }}
                >
                  <div
                    key={wTotalFlashKey + 5000}
                    className={`text-2xl font-black tabular-nums font-orbitron ${wTotalFlashKey > 0 ? 'score-flash' : ''}`}
                    style={{ color: isWhiteWin ? '#e8e8e8' : '#909090', opacity: isWhiteWin ? 1 : 0.55 }}
                  >
                    {animW}
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#e0e0e0]" style={{ opacity: isWhiteWin ? 1 : 0.4 }} />
                    <span className="text-[#555] text-[9px] tracking-widest">白隊</span>
                  </div>
                </div>
              </div>

              {/* Dominance intensity bar */}
              {!isTied && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[#333] text-[9px] tracking-widest uppercase">Dominance</span>
                    <span className="text-[9px] font-bold tracking-widest font-orbitron" style={{ color: `rgba(${leaderRgb},0.6)` }}>
                      {Math.round(gapIntensity * 100)}%
                    </span>
                  </div>
                  <div className="h-1 bg-[#111] rounded-full overflow-hidden border border-[#1a1a1a]">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${gapIntensity * 100}%`, background: `linear-gradient(90deg, rgba(${leaderRgb},0.4), rgba(${leaderRgb},1))`, boxShadow: `0 0 6px rgba(${leaderRgb},${(gapIntensity * 0.8).toFixed(2)})` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ── Desktop layout ── */}
            <div className="hidden md:flex md:flex-row md:items-center md:gap-6">
              {/* Crown icon */}
              <div className="flex-shrink-0 text-center">
                {isTied ? (
                  <div className="w-20 h-20 rounded-full border-2 border-[#2a2a2a] flex items-center justify-center mx-auto">
                    <i className="ri-scales-3-line text-3xl text-[#444]" />
                  </div>
                ) : (
                  <div
                    className="w-20 h-20 rounded-full border-2 flex items-center justify-center mx-auto relative transition-all duration-700"
                    style={{ borderColor: leaderHex, background: `rgba(${leaderRgb},${(0.08 + gapIntensity * 0.12).toFixed(2)})`, boxShadow: `0 0 ${Math.round(gapIntensity * 30)}px rgba(${leaderRgb},${(gapIntensity * 0.5).toFixed(2)})` }}
                  >
                    <i className="ri-crown-fill text-4xl" style={{ color: leaderHex }} />
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: leaderHex }}>
                      <span className="text-[#0d0d0d] text-[9px] font-black">#1</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Status text */}
              <div className="flex-1 text-left">
                {isTied ? (
                  <>
                    <div className="text-[#555] text-xs tracking-[0.4em] uppercase mb-1">Current Standing</div>
                    <div className="text-white text-2xl font-black mb-1 font-serif">勢均力敵</div>
                    <div className="text-[#555] text-sm">兩隊積分持平，最終一戰決勝負</div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-1">
                      <div className="text-xs tracking-[0.4em] uppercase" style={{ color: leaderHex }}>
                        {isBlackWin ? 'Black Team' : 'White Team'} · 暫時領先
                      </div>
                      {dominanceLabel && (
                        <span className="text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full border font-cinzel" style={{ color: dominanceLabel.color, borderColor: dominanceLabel.color, background: `rgba(${leaderRgb},0.08)` }}>
                          {dominanceLabel.text}
                        </span>
                      )}
                    </div>
                    <div className="text-white text-2xl font-black mb-2 font-serif">
                      {isBlackWin ? '黑隊' : '白隊'}
                      <span className="text-sm font-normal text-[#666] ml-2">目前領先</span>
                    </div>
                    <div className="text-[#666] text-sm flex items-center gap-1.5">
                      領先對手{' '}
                      <span
                        className="font-black tabular-nums transition-all duration-500 font-orbitron"
                        style={{ color: leaderHex, fontSize: `${1 + gapIntensity * 0.5}rem` }}
                      >
                        {gap}
                      </span>
                      {' '}分 — 保持壓力！
                    </div>

                    {/* Dominance bar */}
                    <div className="mt-3 w-48">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[#333] text-[9px] tracking-widest uppercase">Dominance</span>
                        <span className="text-[9px] font-bold font-orbitron" style={{ color: `rgba(${leaderRgb},0.6)` }}>
                          {Math.round(gapIntensity * 100)}%
                        </span>
                      </div>
                      <div className="h-1 bg-[#111] rounded-full overflow-hidden border border-[#1a1a1a]">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${gapIntensity * 100}%`, background: `linear-gradient(90deg, rgba(${leaderRgb},0.4), rgba(${leaderRgb},1))`, boxShadow: `0 0 6px rgba(${leaderRgb},${(gapIntensity * 0.8).toFixed(2)})` }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Score chips — desktop */}
              <div className="flex flex-col gap-3 flex-shrink-0">
                <div
                  className="rounded-lg px-5 py-3 text-center border transition-all duration-500"
                  style={{ borderColor: isBlackWin ? `rgba(212,168,67,${borderAlpha})` : '#d4a84330', background: isBlackWin ? `rgba(212,168,67,${(0.06 + gapIntensity * 0.12).toFixed(2)})` : '#0f0f0f' }}
                >
                  <div
                    key={bTotalFlashKey}
                    className={`text-3xl font-black tabular-nums font-orbitron ${bTotalFlashKey > 0 ? 'score-flash' : ''}`}
                    style={{ color: isBlackWin ? '#d4a843' : '#a07830', opacity: isBlackWin ? 1 : 0.55 }}
                  >
                    {animB}
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-[#d4a843]" style={{ opacity: isBlackWin ? 1 : 0.4 }} />
                    <span className="text-[#555] text-[10px] tracking-widest">黑隊</span>
                  </div>
                </div>
                <div
                  className="rounded-lg px-5 py-3 text-center border transition-all duration-500"
                  style={{ borderColor: isWhiteWin ? `rgba(232,232,232,${borderAlpha})` : '#e0e0e025', background: isWhiteWin ? `rgba(255,255,255,${(0.04 + gapIntensity * 0.10).toFixed(2)})` : '#0f0f0f' }}
                >
                  <div
                    key={wTotalFlashKey + 5000}
                    className={`text-3xl font-black tabular-nums font-orbitron ${wTotalFlashKey > 0 ? 'score-flash' : ''}`}
                    style={{ color: isWhiteWin ? '#e8e8e8' : '#909090', opacity: isWhiteWin ? 1 : 0.55 }}
                  >
                    {animW}
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-[#e0e0e0]" style={{ opacity: isWhiteWin ? 1 : 0.4 }} />
                    <span className="text-[#555] text-[10px] tracking-widest">白隊</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PK bars */}
        <div className="border border-[#16172a] rounded-xl p-4 sm:p-6 md:p-8" style={{ background: '#090a14' }}>
          <div className="flex items-center mb-6">
            <div className="w-10 text-right">
              <i className="ri-shield-fill text-xs text-[#d4a843]" />
            </div>
            <div className="flex-1 text-center">
              <div className="flex justify-between px-3 text-[10px] tracking-widest">
                <span className="text-[#d4a843] font-bold">黑隊</span>
                <span className="text-[#555]">各項目 PK</span>
                <span className="text-[#888] font-bold">白隊</span>
              </div>
            </div>
            <div className="w-10">
              <i className="ri-sword-fill text-xs text-[#888]" />
            </div>
          </div>
          {CATEGORIES.map(cat => (
            <PKBar
              key={cat.key}
              label={cat.label}
              icon={cat.icon}
              color={cat.color}
              blackVal={scores.black[cat.key]}
              whiteVal={scores.white[cat.key]}
            />
          ))}
        </div>

        {/* Runner-up row */}
        {!isTied && (
          <div className="mt-3 rounded-lg border border-[#16172a] px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-3" style={{ background: '#090a14' }}>
            <div className="w-9 h-9 flex items-center justify-center rounded-full border border-[#2a2a2a] flex-shrink-0">
              <i className="ri-medal-line text-[#555] text-base" />
            </div>
            <div>
              <div className="text-[#444] text-[10px] tracking-widest uppercase mb-0.5">Runner-up · 目前亞軍</div>
              <div className="text-[#888] text-sm font-bold">
                {isBlackWin ? '白隊' : '黑隊'}
                <span className="text-[#444] font-normal ml-2 text-xs">落後 {gap} 分 — 還有機會！</span>
              </div>
            </div>
            <div className="ml-auto text-2xl font-black tabular-nums font-orbitron" style={{ color: '#555' }}>
              {isBlackWin ? animW : animB}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
